import { DeleteOutlined } from '@ant-design/icons'
import {
  EEvolveLandingPageTabs,
  PLACEHOLDER_IMAGE_AVATAR,
  thunkActionLoading,
} from '@configs'
import {
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import {
  addPostToLibraryAction,
  getLibraryAction,
  useAppDispatch,
  useAppSelector,
} from '@redux'
import { message, TableProps, Tooltip } from 'antd'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { IPostWrapper } from 'src/interfaces/evolve-landing-management'

interface ITabItemProps {
  activeTab?: string
}

export const useLibrary = ({ activeTab }: ITabItemProps) => {
  const dispatch = useAppDispatch()

  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false)
  const [selectedRoadMap, setSelectedRoadMap] = useState<IPostWrapper | null>(
    null
  )
  const [showModal, setShowModal] = useState(false)
  const [dataSource, setDataSource] = useState<IPostWrapper[]>([])
  const [itemMovedID, setItemMovedID] = useState<any>()
  const [selectedItemId, setSelectedItemId] = useState<string>('')

  const isDeletingPostOutLibrary = useAppSelector(
    (state) =>
      state.evolveLanding.loadings[thunkActionLoading.ADD_POST_LIBRARY_LOADING]
  )

  const libraryDetail = useAppSelector((state) => state.evolveLanding.library)
  const isGetLibraryLoading = useAppSelector(
    (state) =>
      state.evolveLanding.loadings[thunkActionLoading.GET_LIBRARY_LOADING]
  )

  const onClose = () => {
    setShowModal(false)
    setSelectedRoadMap(null)
  }

  const onCloseConfirmModal = useCallback(() => {
    setOpenConfirmDeleteModal(false)
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
        delay: 250,
        tolerance: 5,
      },
    })
  )

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setItemMovedID(active.id)
      setDataSource((prev: any) => {
        const activeIndex = prev.findIndex((i: any) => i.postId === active.id)
        const overIndex = prev.findIndex((i: any) => i.postId === over?.id)
        return arrayMove(prev, activeIndex, overIndex)
      })
    }
  }

  const onDeletePostOutLibrary = async () => {
    try {
      if (selectedItemId) {
        const response = await dispatch(
          addPostToLibraryAction({
            posts: dataSource
              .filter((item) => item.postId.toString() !== selectedItemId)
              .map((item) => ({ postId: item.postId, index: item.index })),
          })
        ).unwrap()
        if (response?.success) {
          message.success('Delete successfully')
          setOpenConfirmDeleteModal(false)
          dispatch(getLibraryAction())
        }
      } else {
      }
    } catch (error: any) {
      message.error(error.message)
    }
  }

  const columns: TableProps<IPostWrapper>['columns'] = [
    {
      dataIndex: 'index',
      align: 'center',
      key: 'test',
      width: '4%',
    },
    {
      dataIndex: 'media',
      align: 'center',
      key: 'media',
      width: '4%',
      title: 'Image ',
      render: (_, record) => {
        if (record) {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={record?.post?.thumbnail?.url ?? PLACEHOLDER_IMAGE_AVATAR}
                alt={record?.post?.thumbnail?.url}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        } else {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={PLACEHOLDER_IMAGE_AVATAR}
                alt={'placeholder'}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        }
      },
    },

    {
      dataIndex: 'title',
      align: 'center',
      key: 'title',
      width: '4%',
      title: 'Title EN',
      render: (_, record) => {
        return <p className="line-clamp-2">{record.post.title}</p>
      },
    },

    {
      dataIndex: 'category',
      align: 'center',
      key: 'category',
      width: '4%',
      title: 'Category',
      render: (_, record) => {
        return <p className="line-clamp-2">{record.post.category}</p>
      },
    },
    {
      dataIndex: 'date',
      align: 'center',
      key: 'date',
      width: '4%',
      title: 'Date',
      render: (_, record) => {
        return (
          <p className="line-clamp-2">
            {moment(record.post.postDate).format('MMMM DD, YYYY')}
          </p>
        )
      },
    },

    {
      dataIndex: 'action',
      align: 'center',
      key: 'action',
      width: '4%',
      title: 'Action',
      render: (_, record: IPostWrapper) => {
        return (
          <div className="flex justify-center w-full space-x-4">
            <Tooltip title={'Delete'}>
              <DeleteOutlined
                className={`text-lg font-light mr-2.5 cursor-pointer  hover:opacity-80 !text-red-600`}
                onClick={() => {
                  setOpenConfirmDeleteModal(true)
                  setSelectedItemId(record?.postId.toString())
                }}
              />
            </Tooltip>
          </div>
        )
      },
      ellipsis: true,
    },
  ]

  useEffect(() => {
    if (activeTab === EEvolveLandingPageTabs.LIBRARY) {
      const getMemberUseList = async () => {
        await dispatch(getLibraryAction()).unwrap()
      }
      getMemberUseList()
    }
  }, [activeTab])

  useEffect(() => {
    if (libraryDetail?.id) {
      const posts = libraryDetail.metaData.posts
      const a = [...posts]?.sort((a, b) => a.index - b.index)
      setDataSource(a)
    }
  }, [libraryDetail])

  useEffect(() => {
    const updatePosition = async () => {
      if (itemMovedID) {
        try {
          const newArrArraged = dataSource.map((item, index) => ({
            postId: item.postId,
            index: index + 1,
          }))
          const res = await dispatch(
            addPostToLibraryAction({
              posts: newArrArraged,
            })
          ).unwrap()
          if (res.success) {
            message.success({
              content: 'Update position successfully',
            })
          }
          setItemMovedID(null)
        } catch (error: any) {
          message.success({
            content: 'Update position unsuccessfully',
          })
        }
      }
    }
    updatePosition()
  }, [itemMovedID])

  return {
    columns,
    setShowModal,
    sensors,
    onDragEnd,
    showModal,
    openConfirmDeleteModal,
    onCloseConfirmModal,
    listPosts: dataSource,
    onClose,
    isGetLibraryLoading,
    onDeletePostOutLibrary,
    isDeletingPostOutLibrary,
    selectedRoadMap,
  }
}
